import { Link, useNavigate } from 'react-router-dom';
import { MessagesSquare, Plus } from 'lucide-react';
import { useSupportRequests, useUnreadRequests } from '../../hooks/useSupportRequests';
import { TYPE_LABEL, type SupportRequest } from '../../services/supportRequest.service';
import { StatusBadge, RelativeTime } from '../../pages/requests/requestUi';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Skeleton } from '../ui/skeleton';

/** Requests shown on the home page before sending the user to the full list. */
const MAX_ROWS = 5;

function RequestRow({
  request,
  unreadCount,
  onClick,
}: {
  request: SupportRequest;
  unreadCount: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left flex items-center gap-3 px-6 py-3.5 border-b border-border last:border-b-0 hover:bg-accent/50 transition-colors"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`text-sm truncate ${
              unreadCount > 0 ? 'font-semibold text-foreground' : 'font-medium text-foreground'
            }`}
          >
            {request.title}
          </span>
          {unreadCount > 0 && (
            <Badge variant="default" className="h-5 px-1.5 text-[11px] flex-shrink-0">
              {unreadCount} new
            </Badge>
          )}
          <Badge
            variant="outline"
            className="h-5 px-1.5 text-[11px] font-normal flex-shrink-0 hidden sm:inline-flex"
          >
            {TYPE_LABEL[request.request_type]}
          </Badge>
        </div>
        {request.last_message_preview && (
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {request.last_message_author_label && (
              <>
                <span
                  className={
                    request.last_message_author_type === 'pliable'
                      ? 'font-medium text-blue-700 dark:text-blue-400'
                      : 'font-medium text-foreground'
                  }
                >
                  {request.last_message_author_label}
                </span>
                {': '}
              </>
            )}
            {request.last_message_preview}
          </p>
        )}
      </div>

      <span className="text-xs text-muted-foreground flex items-center gap-1 flex-shrink-0">
        <MessagesSquare className="h-3.5 w-3.5" />
        {request.message_count}
      </span>

      <div className="flex-shrink-0">
        <StatusBadge status={request.status} />
      </div>

      <span className="w-20 flex-shrink-0 whitespace-nowrap text-right text-xs text-muted-foreground">
        <RelativeTime iso={request.last_message_at || request.created_at} />
      </span>
    </button>
  );
}

/**
 * "Open requests" — a compact take on the requests table: everything still in
 * flight with the Pliable team, i.e. anything not done or declined.
 */
export default function OpenRequests() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useSupportRequests({
    status: 'open',
    pageSize: MAX_ROWS,
  });
  const { data: unread } = useUnreadRequests();

  const records = data?.records ?? [];
  // The rows are capped at MAX_ROWS, so the badge has to count the server's total
  // rather than what's on screen.
  const openCount = data?.total_records ?? records.length;

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between px-6 pt-5 pb-1">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-foreground">Open requests</h2>
          {openCount > 0 && <Badge variant="default">{openCount}</Badge>}
          {!!unread?.total && (
            <Badge variant="secondary">{unread.total} unread</Badge>
          )}
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to="/requests">View All</Link>
        </Button>
      </div>
      {/* "Open" spans awaiting_pliable, in_progress *and* awaiting_customer, so this
          can't claim Pliable is working on all of them — some are waiting on you. */}
      <p className="px-6 pb-4 text-sm text-muted-foreground">
        What the Pliable team is working on, and anything waiting on you.
      </p>

      <div className="border-t border-border">
        {isLoading ? (
          <div className="px-6 py-4 space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-3.5 flex-1 max-w-64" />
                <Skeleton className="h-5 w-20 rounded-full" />
                <Skeleton className="h-3 w-14" />
              </div>
            ))}
          </div>
        ) : error ? (
          <p className="px-6 py-8 text-sm text-destructive text-center">
            Failed to load requests.
          </p>
        ) : records.length === 0 ? (
          <div className="px-6 py-10 text-center">
            <p className="text-sm text-muted-foreground">
              Nothing open right now. Start a request when you need a data source connected,
              something changed, or a number checked.
            </p>
            <Link
              to="/requests"
              className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
            >
              <Plus className="h-4 w-4" />
              New request
            </Link>
          </div>
        ) : (
          records.map((req) => (
            <RequestRow
              key={req.id}
              request={req}
              unreadCount={unread?.unread?.[req.id] ?? 0}
              onClick={() => navigate(`/requests/${req.id}`)}
            />
          ))
        )}
      </div>
    </Card>
  );
}
